import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha
} from '@mui/material';
import {
  Timer as TimerIcon,
  WarningAmber as WarningIcon,
  Login as LoginIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  getBreakUntilMs,
  clearBreakSession,
  isBreakOverrunLogged,
  markBreakOverrunLogged,
  getBreakPlannedSeconds,
  getBreakStartedAtMs,
  getBreakPanelClosed,
  setBreakPanelClosed
} from '../../utils/breakSessionStorage';
import { formatSecondsToHms } from '../../utils/breakDurationHms';

/**
 * Full-screen break countdown / overdue prompt. State is driven by `breakSessionStorage`
 * (set when user logs a timed break from the header).
 */
export default function BreakCountdownOverlay() {
  const [tick, setTick] = useState(0);
  const [lsNonce, setLsNonce] = useState(0);
  const [panelClosed, setPanelClosed] = useState(getBreakPanelClosed);
  const [punchingIn, setPunchingIn] = useState(false);
  const [endingEarly, setEndingEarly] = useState(false);
  const [earlyConfirmOpen, setEarlyConfirmOpen] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const bump = () => {
      setLsNonce((n) => n + 1);
      setPanelClosed(getBreakPanelClosed());
    };
    window.addEventListener('erp4cBreakSessionStarted', bump);
    window.addEventListener('punchStatusChanged', bump);
    window.addEventListener('erp4cBreakPanelReopen', bump);
    return () => {
      window.removeEventListener('erp4cBreakSessionStarted', bump);
      window.removeEventListener('punchStatusChanged', bump);
      window.removeEventListener('erp4cBreakPanelReopen', bump);
    };
  }, []);

  void tick;
  void lsNonce;
  const untilMs = getBreakUntilMs();
  const planned = getBreakPlannedSeconds();

  const logOverrunIfNeeded = useCallback(async (minutesLate) => {
    if (isBreakOverrunLogged()) return;
    try {
      const plannedLabel = planned != null ? formatSecondsToHms(planned) : 'unknown';
      await api.post('/activities/quick', {
        activity_type: 'break_overrun',
        notes: `Returned ${minutesLate} minute(s) after the scheduled break ended. Planned break duration was ${plannedLabel}.`,
        duration_minutes: minutesLate
      });
      markBreakOverrunLogged();
    } catch (e) {
      console.error('break_overrun log failed', e);
    }
  }, [planned]);

  useEffect(() => {
    if (!untilMs) {
      setBreakPanelClosed(false);
      setPanelClosed(false);
      return;
    }
    const now = Date.now();
    if (now < untilMs) return;
    const minutesLate = Math.max(1, Math.ceil((now - untilMs) / 60000));
    logOverrunIfNeeded(minutesLate);
    setBreakPanelClosed(false);
    setPanelClosed(false);
  }, [untilMs, tick, logOverrunIfNeeded]);

  if (!untilMs) return null;

  const now = Date.now();
  const remainingMs = untilMs - now;
  const overdue = remainingMs <= 0;
  const remainingSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const overdueMin = overdue ? Math.max(1, Math.ceil(-remainingMs / 60000)) : 0;

  if (!overdue && panelClosed) {
    return null;
  }

  const resolveBreakStartedAt = () => {
    const direct = getBreakStartedAtMs();
    if (direct != null) return direct;
    const pl = getBreakPlannedSeconds();
    const u = getBreakUntilMs();
    if (pl != null && u != null) return u - pl * 1000;
    return Date.now();
  };

  const handlePunchIn = async () => {
    setPunchingIn(true);
    try {
      await api.post('/punchcard/punch-in');
      clearBreakSession();
      setBreakPanelClosed(false);
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'punch-in' } }));
    } catch (e) {
      console.error(e);
    } finally {
      setPunchingIn(false);
    }
  };

  const handleHideDuringCountdown = () => {
    setBreakPanelClosed(true);
    setPanelClosed(true);
  };

  const handleConfirmEndBreakEarly = async () => {
    setEarlyConfirmOpen(false);
    setEndingEarly(true);
    try {
      const started = resolveBreakStartedAt();
      const actualSec = Math.max(1, Math.floor((Date.now() - started) / 1000));
      const plannedSec = planned != null ? planned : null;
      const actualHms = formatSecondsToHms(actualSec);
      const plannedHms = plannedSec != null ? formatSecondsToHms(plannedSec) : 'n/a';
      const notes = `Break ended before scheduled time. Actual time away: ${actualHms} (planned ${plannedHms}).`;

      await api.post('/punchcard/punch-in');
      clearBreakSession();
      setBreakPanelClosed(false);
      window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'break-ended-early' } }));
      try {
        await api.post('/activities/quick', {
          activity_type: 'break_taken_early',
          notes,
          duration_minutes: Math.max(1, Math.ceil(actualSec / 60)),
          break_duration_hms: actualHms,
          break_duration_seconds: actualSec,
          break_planned_end_at: new Date(untilMs).toISOString()
        });
      } catch (logErr) {
        console.error('break_taken_early log failed', logErr);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setEndingEarly(false);
    }
  };

  return (
    <>
      <Dialog
        open={earlyConfirmOpen}
        onClose={() => !endingEarly && setEarlyConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            End break and return to work?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You will be punched in immediately. The time from the start of this break until now (
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
              {formatSecondsToHms(Math.max(1, Math.floor((Date.now() - resolveBreakStartedAt()) / 1000)))}
            </Box>
            ) will be recorded as the break you took. You cannot save other work while on a break.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEarlyConfirmOpen(false)} disabled={endingEarly}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmEndBreakEarly} disabled={endingEarly}>
            {endingEarly ? <CircularProgress size={22} /> : 'End break & punch in'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={overdue}
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            border: (theme) =>
              overdue ? `2px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`
          }
        }}
      >
        <DialogContent sx={{ py: 4, px: 3, textAlign: 'center' }}>
          {!overdue ? (
            <>
              <TimerIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                Break timer
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Time remaining until your scheduled break ends (punched out — clock below shows work status). Other
                saves are blocked until you punch in or end the break early.
              </Typography>
              <Typography
                variant="h2"
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 800,
                  letterSpacing: 2,
                  fontSize: { xs: '2.75rem', sm: '3.75rem' },
                  color: 'primary.main',
                  lineHeight: 1.1,
                  mb: 2
                }}
              >
                {formatSecondsToHms(remainingSec)}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'stretch', maxWidth: 360, mx: 'auto' }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={punchingIn ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                  onClick={handlePunchIn}
                  disabled={punchingIn || endingEarly}
                >
                  I&apos;m back — punch in now
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={endingEarly ? <CircularProgress size={18} /> : <UndoIcon />}
                  onClick={() => setEarlyConfirmOpen(true)}
                  disabled={punchingIn || endingEarly}
                >
                  Cancel break — return early
                </Button>
                <Button variant="text" onClick={handleHideDuringCountdown} disabled={punchingIn || endingEarly}>
                  Hide timer
                </Button>
                <Typography variant="caption" color="text.secondary">
                  If you hide this, the next tap anywhere will open it again — you still cannot save work until the
                  break is finished. Come back a little before zero so you can punch in on time.
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Box
                component="img"
                alt=""
                src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='120' viewBox='0 0 160 120'%3E%3Crect fill='%23fee2e2' width='160' height='120' rx='12'/%3E%3Ctext x='50%25' y='52%25' dominant-baseline='middle' text-anchor='middle' fill='%23b91c1c' font-family='sans-serif' font-size='14' font-weight='700'%3EBREAK ENDED%3C/text%3E%3Ctext x='50%25' y='72%25' dominant-baseline='middle' text-anchor='middle' fill='%237f1d1d' font-family='sans-serif' font-size='11'%3EPlease punch in%3C/text%3E%3C/svg%3E"
                sx={{ maxWidth: '100%', height: 'auto', mb: 2, borderRadius: 2 }}
              />
              <WarningIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'error.main', mb: 1 }}>
                Break time ended
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are{' '}
                <Box component="span" sx={{ fontWeight: 800, color: 'error.main' }}>
                  {overdueMin} minute{overdueMin === 1 ? '' : 's'}
                </Box>{' '}
                past your scheduled break return.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This has been logged to your activity. Punch in now to resume your session.
              </Typography>
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={punchingIn ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                onClick={handlePunchIn}
                disabled={punchingIn}
                sx={{ boxShadow: (theme) => `0 8px 24px ${alpha(theme.palette.error.main, 0.35)}` }}
              >
                Punch in now
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
